import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ONBOARDING_STEP_DEFS, type OnboardingDto, type OnboardingUpdate } from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { IconBuilding, IconCircle, IconDone, IconHelp, IconPlus, IconTrash } from "../../app/icons";
import { api, ApiError } from "../../lib/api";
import { useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";

/** Aan de slag (issue #162): welkomstscherm, checklist en voorbeeldgebouw voor een nieuwe organisatie. */
export function useOnboarding() {
  const { data: me } = useMe();
  return useQuery({
    queryKey: ["onboarding", me?.org?.id],
    queryFn: () => api<OnboardingDto>("/onboarding"),
    enabled: Boolean(me?.org),
  });
}

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

function useOnboardingActions() {
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["onboarding"] }),
      qc.invalidateQueries({ queryKey: ["buildings"] }),
    ]);
  return {
    refresh,
    async update(body: OnboardingUpdate) {
      await api("/onboarding", { method: "PATCH", body });
      await qc.invalidateQueries({ queryKey: ["onboarding"] });
    },
    async addDemo(): Promise<string | null> {
      try {
        const { id } = await api<{ id: string }>("/onboarding/demo", { method: "POST" });
        await refresh();
        return id;
      } catch (e) {
        toast(errMsg(e));
        return null;
      }
    },
    async removeDemo() {
      try {
        await api("/onboarding/demo", { method: "DELETE" });
        toast("Voorbeeldgebouw verwijderd");
        await refresh();
      } catch (e) {
        toast(errMsg(e));
      }
    },
  };
}

/** Checklist op Gebouwen: vinkt zichzelf af op basis van echte gegevens, het voorbeeldgebouw telt niet mee. */
export function OnboardingChecklist({ data }: { data: OnboardingDto }) {
  const actions = useOnboardingActions();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const doneCount = data.steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / Math.max(1, data.steps.length)) * 100);
  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel onboarding" aria-labelledby="ob-title">
      <div className="ob-head">
        <div className="grow">
          <h2 id="ob-title">Aan de slag</h2>
          <p className="muted small">
            {doneCount} van {data.steps.length} klaar. Elke stap vinkt zichzelf af zodra je hem in de app hebt gedaan.
          </p>
        </div>
        <button
          type="button"
          className="btn ghost small"
          onClick={() => void actions.update({ checklistHidden: true })}
        >
          Verbergen
        </button>
      </div>
      <div
        className="ob-bar"
        role="progressbar"
        aria-label="Voortgang Aan de slag"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <span style={{ width: `${pct}%` }} />
      </div>
      <ol className="ob-steps">
        {data.steps.map((s) => {
          const def = ONBOARDING_STEP_DEFS[s.key];
          return (
            <li key={s.key} className={s.done ? "done" : undefined}>
              <Link to={s.href} className="ob-step">
                {s.done ? (
                  <IconDone size={18} aria-hidden="true" className="ob-icon" />
                ) : (
                  <IconCircle size={18} aria-hidden="true" className="ob-icon" />
                )}
                <span className="grow">
                  <strong>{def.title}</strong>
                  <span className="muted small">{def.description}</span>
                </span>
                <span className="sr-only">{s.done ? "(klaar)" : "(nog te doen)"}</span>
              </Link>
            </li>
          );
        })}
      </ol>
      <div className="ob-foot">
        {data.demoBuildingId ? (
          <>
            <span className="grow small">
              <IconBuilding size={16} aria-hidden="true" /> Kijk rond in het{" "}
              <Link to={`/gebouwen/${data.demoBuildingId}`}>voorbeeldgebouw</Link>. Het telt niet mee voor je
              abonnement.
            </span>
            <button type="button" className="btn ghost small" disabled={busy} onClick={run(actions.removeDemo)}>
              <IconTrash size={16} aria-hidden="true" />
              Voorbeeld verwijderen
            </button>
          </>
        ) : (
          <>
            <span className="grow small muted">Eerst rondkijken met voorbeeldgegevens?</span>
            <button
              type="button"
              className="btn ghost small"
              disabled={busy}
              onClick={run(async () => {
                const id = await actions.addDemo();
                if (id) navigate(`/gebouwen/${id}`);
              })}
            >
              <IconPlus size={16} aria-hidden="true" />
              {busy ? "Klaarzetten…" : "Voorbeeldgebouw toevoegen"}
            </button>
          </>
        )}
        <Link to="/hulp" className="btn ghost small">
          <IconHelp size={16} aria-hidden="true" />
          Hulp en uitleg
        </Link>
      </div>
    </section>
  );
}

const WELCOME_STEPS = ["Welkom", "Collega's", "Beginnen"] as const;

/**
 * Welkomstscherm bij de eerste keer inloggen (eigenaar of beheerder): in drie stappen naar een eerste gebouw,
 * met collega's erbij en een voorbeeldgebouw om rond te kijken. Overslaan kan altijd.
 */
export function WelcomeDialog({ orgName, onNewBuilding }: { orgName: string; onNewBuilding: () => void }) {
  const actions = useOnboardingActions();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [emails, setEmails] = useState("");
  const [demo, setDemo] = useState(true);
  const [busy, setBusy] = useState(false);

  const list = emails
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
  const invalid = list.filter((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));

  async function invite() {
    if (!list.length) return setStep(2);
    if (invalid.length) return toast(`Controleer: ${invalid.join(", ")}`);
    setBusy(true);
    let sent = 0;
    for (const email of list) {
      try {
        await api("/org/invitations", { method: "POST", body: { email, role: "beheerder" } });
        sent++;
      } catch (e) {
        toast(`${email}: ${errMsg(e)}`);
      }
    }
    setBusy(false);
    if (sent) toast(sent === 1 ? "Uitnodiging verstuurd" : `${sent} uitnodigingen verstuurd`);
    setStep(2);
  }

  async function finish(next: "building" | "demo" | "skip") {
    setBusy(true);
    try {
      await actions.update({ welcomeDone: true });
      const demoId = next === "demo" || (next === "building" && demo) ? await actions.addDemo() : null;
      if (next === "demo" && demoId) navigate(`/gebouwen/${demoId}`);
      if (next === "building") onNewBuilding();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  const footer =
    step === 0 ? (
      <>
        <button type="button" className="btn ghost" onClick={() => void finish("skip")} disabled={busy}>
          Overslaan
        </button>
        <button type="button" className="btn primary" onClick={() => setStep(1)}>
          Volgende
        </button>
      </>
    ) : step === 1 ? (
      <>
        <button type="button" className="btn ghost" onClick={() => setStep(0)} disabled={busy}>
          Terug
        </button>
        <button type="button" className="btn primary" onClick={() => void invite()} disabled={busy}>
          {list.length ? (busy ? "Bezig met uitnodigen…" : "Uitnodigen en verder") : "Later, verder"}
        </button>
      </>
    ) : (
      <>
        <button type="button" className="btn" onClick={() => void finish("demo")} disabled={busy}>
          Eerst rondkijken
        </button>
        <button type="button" className="btn primary" onClick={() => void finish("building")} disabled={busy}>
          Eerste gebouw toevoegen
        </button>
      </>
    );

  return (
    <Modal title="Welkom bij Klantpunt" onClose={() => void finish("skip")} busy={busy} footer={footer}>
      <ol className="ob-dots" aria-label="Stappen">
        {WELCOME_STEPS.map((s, i) => (
          <li key={s} className={i === step ? "on" : i < step ? "done" : undefined} aria-current={i === step}>
            {s}
          </li>
        ))}
      </ol>
      {step === 0 && (
        <div className="ob-welcome">
          <p>
            De omgeving voor <strong>{orgName}</strong> staat klaar. In een paar minuten heb je je eerste gebouw met
            plattegrond in Klantpunt.
          </p>
          <ul>
            <li>Zet bevindingen op de plattegrond, met foto en prioriteit.</li>
            <li>Houd assets, keuringen en onderhoud per gebouw bij.</li>
            <li>Laat gebruikers via een QR-code melden, zonder in te loggen.</li>
          </ul>
        </div>
      )}
      {step === 1 && (
        <>
          <p>Werk je samen met collega's die ook gebouwen beheren? Nodig ze nu uit, dan krijgen ze een mail.</p>
          <label className="field">
            <span className="label">E-mailadressen</span>
            <textarea
              rows={3}
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="naam@bedrijf.nl, collega@bedrijf.nl"
              autoComplete="off"
            />
            <span className="muted small">
              Ze worden beheerder en zien alle gebouwen. Medewerkers en klanten met beperkte toegang nodig je later uit
              via Gebruikers.
            </span>
          </label>
        </>
      )}
      {step === 2 && (
        <>
          <p>
            Voeg je eerste gebouw toe met naam en adres, daarna upload je de plattegrond. Of kijk eerst rond in een
            voorbeeld: een kantoorgebouw met plattegronden, bevindingen, assets en keuringen.
          </p>
          <label className="check">
            <input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} disabled={busy} />
            <span>Zet ook een voorbeeldgebouw klaar (later met één klik te verwijderen)</span>
          </label>
          {busy && <p className="muted small">Even geduld, we zetten alles klaar…</p>}
        </>
      )}
    </Modal>
  );
}
